<script setup lang="ts">
import type { SeoBreadcrumb } from '@/types/cms';

defineOptions({
  name: 'ThankYou',
});

const { $urls, $mopI18n, $storyblokLivePreview } = useNuxtApp();
const route = useRoute();

const classNameRef = ref(['']);
const contentPath = '/career/start-creating-with-us/our-jobs/thank-you';
const { getCmsStory, trackPageView, cmsStoryModelRef, loadingRef } = useMopCms(contentPath);
const { getBreadcrumbSchema } = useMopSeo();
const { initTransition } = useMopPageTransitionClient();
const { handleHeadForCmsPage } = useMopSeo();
const jobId = route.query.jobId as string;
const { fetchJob } = useMopJob(jobId);

initTransition(classNameRef, loadingRef);

useAsyncData(
  () => getAsyncDatatCacheId(route),
  async () => {
    const language = await fetchJob(jobId);
    await getCmsStory(contentPath, {
      language: language || 'default',
    });

    if (!cmsStoryModelRef.value.isInitialized()) {
      return null;
    }
    if (cmsStoryModelRef.value.hasErrorNotFound()) {
      throw createError({
        statusCode: 404,
        message: 'PAGE_NOT_FOUND',
      });
    }
    return null;
  },
);

$storyblokLivePreview.initStoryListener('Storyblok', cmsStoryModelRef);

onMounted(() => {
  trackPageView('Career');
});

const seoHeadRef = computed(() => {
  const careerLink: string = $mopI18n.localePath($urls.CAREER);
  const startCreatingLink: string = $mopI18n.localePath($urls.CAREER_START_CREATING);

  if (!cmsStoryModelRef.value.isInitialized() || cmsStoryModelRef.value.hasError()) {
    return {};
  }

  const breadcrumbs: SeoBreadcrumb[] = [
    {
      name: $mopI18n.t('locale.blog.name_career'),
      url: careerLink,
    },
    {
      name: $mopI18n.t('locale.jobs.start_creating_with_us'),
      url: startCreatingLink,
    },
    {
      name: $mopI18n.t('locale.jobs.thank_you'),
      url: route.path,
    },
  ];

  return {
    ...handleHeadForCmsPage(cmsStoryModelRef.value),
    ...getBreadcrumbSchema(breadcrumbs),
  };
});

useHead(seoHeadRef as any);
</script>

<template>
  <div :class="['thank-you', 'transition', ...classNameRef]">
    <MopPageLoad :class="classNameRef" />
    <div class="transition-content">
      <MopCmsContentElementsSlot id="content-page" :elements="cmsStoryModelRef.getContentElements()" />
    </div>
  </div>
</template>
